/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import '../../styles/components/AsperaAutocomplete.scss';
import { iterableToArray, parseValidEmailsFromString } from '../../helpers/helpers';
import { containsDomChild, observableTimeout } from '../../helpers/starterHelpers';
import { language } from '../../services/LanguageService';
import { SubManager } from '../../classes/SubManager';
import { fromEvent, Subject } from 'rxjs';
import { AsperaError } from '../../classes/AsperaError';
import { debounceTime } from 'rxjs/operators';
import { tracking } from '../../services/TrackingService';
import { ImageSearch } from '../../svg/ImageSearch';
import { FileUploaderItem, Button, Tag, getIcon } from '../Carbon';
/**
 * React Component
 *
 * The AsperaAutocomplete provides the autocomplete functionality for typing and finding items on an API.
 * This component will filter out items already selected from the options.  Consumer can provide full length. Use maxNumberResults to limit length.
 *
 * @example
 * ```javascript
 * <AsperaAutocomplete />
 * ```
 */
export class AsperaAutocomplete extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            focused: false,
            currentText: '',
            resultList: [],
            keyboardItemIndex: 0,
            lastItemUpdate: undefined,
        };
        this.subManager = new SubManager();
        this.hasFocused = false;
        /** Store selected Items. Based on primaryId */
        this.selectedItems = new Map();
        this.textSearchSub = new Subject();
        this.keyboardTapThroughList = [];
        this.mouseEventAvoid = false;
        this.lastMouseEventCoordinates = {
            x: 0,
            y: 0,
        };
        this.changeValues = (clearText) => {
            const { onChange } = this.props;
            if (typeof onChange === 'function')
                onChange(iterableToArray(this.selectedItems.values()));
            this.subManager.setSubscription(observableTimeout(10).subscribe({
                next: () => {
                    this.keyboardTapThroughList = [];
                    const state = {
                        keyboardItemIndex: 0,
                    };
                    if (clearText) {
                        this.clearFocusAndText();
                        state.currentText = '';
                    }
                    this.captureFocus();
                    this.setState(state);
                },
                error: error => {
                    new AsperaError('AsperaAutocomplete: Unable to wait for safe click outside event', error);
                },
            }));
        };
        this.clearList = (event) => {
            const { label } = this.props;
            tracking.track('User Form', {
                action: 'Autocomplete List Cleared',
                field: label,
                successFlag: true,
            });
            event.stopPropagation();
            this.selectedItems.clear();
            this.changeValues();
        };
        this.removeItem = (id) => {
            var _a;
            const { label } = this.props;
            tracking.track('User Form', {
                action: 'Autocomplete Item Removed',
                field: label,
                data: (_a = this.selectedItems.get(id)) === null || _a === void 0 ? void 0 : _a.displayName,
                successFlag: true,
            });
            this.selectedItems.delete(id);
            this.changeValues();
        };
        this.addItem = (item) => {
            const { label, singleItemMode } = this.props;
            if (item.disabled) {
                return;
            }
            tracking.track('User Form', {
                action: 'Autocomplete Item Added',
                field: label,
                data: item.displayName,
                successFlag: true,
            });
            if (singleItemMode) {
                this.selectedItems.clear();
            }
            this.selectedItems.set(item.primaryId, item);
            this.changeValues(true);
        };
        this.getDropdownContent = (item) => {
            const { maxNumberResults, disabled } = this.props;
            const { keyboardItemIndex } = this.state;
            const className = 'autocomplete-results-list-item';
            const resultsToUse = (Array.isArray(item.results) && item.results.length) ? item.results.filter(item => !this.selectedItems.get(item.primaryId)) : [];
            if (item.status === 'loading') {
                return React.createElement("div", { className: className }, language.get('autocomplete.loading'));
            }
            else if (item.status === 'error') {
                return React.createElement("div", { className: className }, language.get('autocomplete.error'));
            }
            else if (resultsToUse.length) {
                return resultsToUse.slice(0, maxNumberResults).map((result, index) => {
                    this.keyboardTapThroughList.push(result);
                    const keyboardIndex = this.keyboardTapThroughList.length - 1;
                    const isActive = keyboardItemIndex === keyboardIndex;
                    return (React.createElement("button", { disabled: result.disabled, key: index, type: "button", onMouseEnter: (event) => {
                            if (result.disabled) {
                                return;
                            }
                            if ((this.lastMouseEventCoordinates.x !== event.pageX || this.lastMouseEventCoordinates.y !== event.pageY) && !this.mouseEventAvoid) {
                                this.lastMouseEventCoordinates.x = event.pageX;
                                this.lastMouseEventCoordinates.y = event.pageY;
                                this.setState({ keyboardItemIndex: keyboardIndex, lastItemUpdate: 'mouse' });
                            }
                            this.mouseEventAvoid = false;
                        }, "data-auto": `autocomplete-result-item-${result.primaryId}`, className: `${className} clickable-item ${isActive ? 'current-selected' : ''}`, onClick: disabled ? undefined : () => {
                            if (result.disabled) {
                                return;
                            }
                            this.hasFocused = true;
                            this.addItem(result);
                        } },
                        !!result.displayImage && React.createElement("div", { className: "autocomplete-image-wrapper", style: { backgroundImage: `url(${result.displayImage})` } }),
                        result.displayName));
                });
            }
            else {
                if (item.noResultsAction) {
                    return (React.createElement("div", { className: `${className} no-results-group` },
                        React.createElement(ImageSearch, null),
                        React.createElement("div", { className: "no-results-group--heading" }, language.get('autocomplete.cannotFindWhatLookingFor')),
                        !!item.noResultsAction.descriptionText && React.createElement("div", { className: "no-results-group--description" }, item.noResultsAction.descriptionText),
                        React.createElement(Button, { disabled: disabled, className: "no-results-group--action", kind: "tertiary", onClick: item.noResultsAction.onClick }, item.noResultsAction.buttonText || language.get('autocomplete.createAndAdd'))));
                }
                return React.createElement("div", { className: className }, language.get('autocomplete.noResults'));
            }
        };
        this.onPaste = (event) => {
            var _a;
            const { reasonToBlockAdding, exchangeEmailPaste, singleItemMode } = this.props;
            const paste = ((_a = (event.clipboardData || window.clipboardData)) === null || _a === void 0 ? void 0 : _a.getData('text')) || '';
            if (typeof exchangeEmailPaste === 'function' && !singleItemMode && !reasonToBlockAdding) {
                const results = exchangeEmailPaste(parseValidEmailsFromString(paste));
                if (results.length) {
                    results.forEach(item => {
                        this.selectedItems.set(item.primaryId, item);
                    });
                    this.changeValues(true);
                }
            }
            this.searchChange(event, paste);
        };
        this.onKeyDown = (event) => {
            const { keyboardItemIndex } = this.state;
            const key = event.key;
            this.hasFocused = true;
            switch (key) {
                case 'ArrowUp':
                case 'Up':
                    if (this.keyboardTapThroughList.length) {
                        let newIndex;
                        if (keyboardItemIndex === 0) {
                            newIndex = this.keyboardTapThroughList.length - 1;
                        }
                        else {
                            newIndex = keyboardItemIndex - 1;
                        }
                        this.setState({ keyboardItemIndex: newIndex, lastItemUpdate: 'keyboard' });
                    }
                    break;
                case 'ArrowDown':
                case 'Down':
                    if (this.keyboardTapThroughList.length) {
                        let newIndex;
                        if (keyboardItemIndex === (this.keyboardTapThroughList.length - 1)) {
                            newIndex = 0;
                        }
                        else {
                            newIndex = keyboardItemIndex + 1;
                        }
                        this.setState({ keyboardItemIndex: newIndex, lastItemUpdate: 'keyboard' });
                    }
                    break;
                case 'Enter':
                case ',':
                    if (this.keyboardTapThroughList.length) {
                        this.addItem(this.keyboardTapThroughList[keyboardItemIndex]);
                    }
                    break;
                case 'Tab':
                    this.clearFocusAndText();
                    break;
            }
            if (this.abortAndPrevent(event))
                event.preventDefault();
        };
        this.searchChange = (event, overrideText) => {
            const { currentText } = this.state;
            const { singleItemMode, listView } = this.props;
            const preventSearchKeys = ['ArrowRight', 'Right', 'ArrowLeft', 'Left'];
            const key = event.key;
            if (this.abortAndPrevent(event) || (preventSearchKeys.indexOf(key) > -1)) {
                event.preventDefault();
                return;
            }
            if (singleItemMode && this.selectedItems.size) {
                this.selectedItems.clear();
                this.changeValues();
            }
            let newCurrentText = overrideText || event.target.innerText;
            if (typeof newCurrentText === 'string')
                newCurrentText = newCurrentText.trim();
            this.setState({ currentText: newCurrentText, focused: true });
            tracking.track('Performed Search', { data: newCurrentText, type: 'Autocomplete Search', successFlag: true });
            this.textSearchSub.next(newCurrentText);
            if (key === 'Backspace' && !currentText && !listView) {
                this.removeLastItem();
            }
        };
        this.focus = () => {
            const { initialResults } = this.props;
            let hasValidInitialResults = false;
            if (initialResults) {
                initialResults.forEach(item => {
                    var _a;
                    (_a = item.results) === null || _a === void 0 ? void 0 : _a.forEach(child => {
                        if (!this.selectedItems.get(child.primaryId)) {
                            hasValidInitialResults = true;
                        }
                    });
                });
            }
            this.setState({ focused: true, resultList: hasValidInitialResults ? initialResults || [] : [] });
        };
        this.captureFocus = () => {
            const { id } = this.props;
            const editArea = document.querySelector(`#${id} .autocomplete-text-box-input`);
            if (editArea) {
                editArea.focus();
            }
        };
    }
    removeLastItem() {
        const listOfItems = iterableToArray(this.selectedItems.keys());
        if (listOfItems.length)
            this.removeItem(listOfItems.pop());
    }
    getDropdown() {
        const { resultList } = this.state;
        this.keyboardTapThroughList = [];
        if (!Array.isArray(resultList) || !resultList.length)
            return undefined;
        return (React.createElement("div", { className: "autocomplete-results-list" }, resultList.map((grouping, index) => {
            return (React.createElement("div", { key: index, className: "autocomplete-results-list-group" },
                !!grouping.title && React.createElement("div", { className: "autocomplete-results-list-group-heading" }, grouping.title),
                React.createElement("div", { className: "autocomplete-results-list-items" }, this.getDropdownContent(grouping))));
        })));
    }
    getListBasedItems() {
        const { light, disabled } = this.props;
        return (React.createElement("div", { className: "autocomplete-list-items" },
            !!this.selectedItems.size && React.createElement("div", { className: "autocomplete-list-items--title" }, language.get('autocomplete.addedList')),
            !!this.selectedItems.size && iterableToArray(this.selectedItems.entries()).map((entry, index) => {
                const id = entry[0];
                const item = entry[1];
                return (React.createElement(FileUploaderItem, { className: `autocomplete-list-items--item cds--file__selected-file ${light ? 'file-container-item--light' : ''} ${disabled ? 'disabled-item' : ''}`, status: "edit", key: index, "data-auto": `autocomplete-added-item-${id}`, name: `${item.displayType ? `${item.displayType} - ` : ''}${item.displayName}`, onDelete: disabled ? undefined : () => {
                        this.removeItem(id);
                    } }));
            })));
    }
    get singleItemView() {
        if (this.selectedItems.size) {
            const items = iterableToArray(this.selectedItems.values());
            return React.createElement("span", { className: "autocomplete-single-item-text" }, items[0].displayName);
        }
        else {
            return null;
        }
    }
    getInnerItems() {
        const { listView, singleItemMode, disabled, label } = this.props;
        return (React.createElement("div", { className: "autocomplete-list-tags" },
            !listView && (singleItemMode ? this.singleItemView : iterableToArray(this.selectedItems.entries()).map((entry, index) => {
                const id = entry[0];
                const item = entry[1];
                return (React.createElement(Tag, { key: index, filter: true, "data-auto": `autocomplete-added-item-${id}`, onClose: disabled ? undefined : () => {
                        this.removeItem(id);
                    }, type: item.displayTagColor || 'blue', className: `list-tag ${disabled ? 'disabled-item' : ''}` },
                    !!item.displayType && React.createElement("span", { className: "list-tag-type" },
                        item.displayType,
                        ":",
                        ' '),
                    " ",
                    item.displayName));
            })),
            React.createElement("div", { className: "autocomplete-text-box" }, !disabled && React.createElement("span", { contentEditable: true, "aria-label": label, role: "textbox", spellCheck: "false", className: "autocomplete-text-box-input", onFocus: this.focus, onKeyUp: this.searchChange, onKeyDown: this.onKeyDown, onPaste: this.onPaste }))));
    }
    scrollToView() {
        const element = document.querySelector('.current-selected');
        if (element)
            element.scrollIntoView(false);
        this.mouseEventAvoid = true;
    }
    /** Indicate if an event for the text box should be prevented and/or stop operation */
    abortAndPrevent(event) {
        // eslint-disable-next-line
        const illegalKeys = ['Enter', 'ArrowUp', 'Up', 'ArrowDown', 'Down'];
        const key = event.key;
        return (illegalKeys.indexOf(key) > -1);
    }
    clearTextInSpan() {
        const { id } = this.props;
        const span = document.querySelector(`#${id} .autocomplete-text-box-input`);
        if (span)
            span.innerText = '';
    }
    clearFocusAndText() {
        this.clearTextInSpan();
        this.setState({ focused: false, currentText: '' });
    }
    componentDidMount() {
        const { initialItems, resultListSub, id, overrideItemsSub, onSearchChange } = this.props;
        this.subManager.setSubscription(resultListSub.subscribe({
            next: data => {
                this.setState({ resultList: data, keyboardItemIndex: 0, lastItemUpdate: undefined });
            },
            error: error => {
                new AsperaError('AsperaAutocomplete: cannot setup click event on document', error);
            },
        }));
        this.subManager.setSubscription(this.textSearchSub.pipe(debounceTime(500)).subscribe({
            next: text => {
                const { reasonToBlockAdding } = this.props;
                if (reasonToBlockAdding) {
                    this.setState({ resultList: [{ title: reasonToBlockAdding, results: [] }] });
                }
                else if (typeof onSearchChange === 'function' && !reasonToBlockAdding) {
                    onSearchChange((text || '').trim());
                }
            },
            error: error => {
                new AsperaError('AsperaAutocomplete: Unable to subscribe to text search subscription', error);
            },
        }));
        if (overrideItemsSub) {
            this.subManager.setSubscription(overrideItemsSub.subscribe({
                next: data => {
                    this.selectedItems.clear();
                    data.forEach(item => {
                        this.selectedItems.set(item.primaryId, item);
                    });
                    this.setState({});
                },
                error: error => {
                    new AsperaError('AsperaAutocomplete: cannot subscribe to override item', error);
                },
            }));
        }
        const elements = [document];
        const modalElement = document.querySelector('.cds--modal');
        if (modalElement) {
            elements.push(modalElement);
        }
        this.subManager.setSubscription(fromEvent(elements, 'click').subscribe({
            next: event => {
                if (!containsDomChild(`#${id}`, event.target))
                    this.clearFocusAndText();
            },
            error: error => {
                new AsperaError('AsperaAutocomplete: cannot setup click event on document', error);
            },
        }));
        this.subManager.setSubscription(fromEvent(document, 'keyup').subscribe({
            next: event => {
                if (event.key === 'Escape') {
                    this.clearFocusAndText();
                    if (document.activeElement && typeof document.activeElement.blur === 'function') {
                        document.activeElement.blur();
                    }
                }
            },
            error: error => {
                new AsperaError('AsperaAutocomplete: cannot setup keyboard event on document', error);
            },
        }));
        if (Array.isArray(initialItems) && initialItems.length) {
            initialItems.forEach(item => {
                this.selectedItems.set(item.primaryId, item);
            });
            this.setState({});
        }
    }
    componentDidUpdate() {
        const { lastItemUpdate } = this.state;
        if (lastItemUpdate === 'keyboard') {
            this.scrollToView();
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { className, label, light, placeholder, helperText, textAreaStyle, id, actionButton, required, listView, disabled } = this.props;
        const { focused, currentText } = this.state;
        const isInvalid = this.hasFocused && required && !currentText && !this.selectedItems.size;
        const autocompleteContent = (React.createElement("div", { className: "aspera-autocomplete-primary-content" },
            React.createElement("div", { className: `autocomplete-form-item cds--text-input ${light ? 'light-mode' : ''} ${focused ? 'autocomplete-focus' : ''} ${textAreaStyle ? 'autocomplete-textarea' : ''} ${isInvalid ? 'autocomplete-invalid' : ''}`, onClick: this.captureFocus },
                getIcon('Search', 20, { className: 'autocomplete-search-icon cds--search-magnifier-icon' }),
                !!(placeholder && !(!!(this.selectedItems.size && !listView) || !!currentText)) && React.createElement("div", { className: "autocomplete-placeholder" }, placeholder),
                React.createElement("div", { className: "autocomplete-list-container" }, this.getInnerItems()),
                !!(this.selectedItems.size && !listView && !disabled) && React.createElement("button", { className: "autocomplete-clear cds--search-close", type: "button", onClick: this.clearList, "data-auto": "autocomplete-clear-all" }, getIcon('Close')))));
        const actionButtonView = actionButton ? React.createElement("div", { className: "aspera-autocomplete-action-wrapper" },
            React.createElement(Button, { kind: actionButton.kind || 'tertiary', disabled: disabled, hasIconOnly: true, renderIcon: actionButton.icon, iconDescription: actionButton.title, onClick: actionButton.onClick, "data-auto": "autocomplete-action-button", tooltipAlignment: "end", tooltipPosition: "bottom" })) : undefined;
        return (
        // eslint-disable-next-line react/destructuring-assignment
        React.createElement("div", { className: `aspera-autocomplete ${className ? className : ''} ${disabled ? 'disabled-autocomplete' : ''} ${light ? 'aspera-autocomplete--light-mode' : ''}`, id: id, "data-auto": this.props['data-auto'] },
            !!label && React.createElement("div", { className: "cds--label" }, label),
            actionButtonView ? (React.createElement("div", { className: "aspera-autocomplete--wrapper-with-action" },
                actionButtonView,
                autocompleteContent)) : autocompleteContent,
            focused && this.getDropdown(),
            (!!helperText && !isInvalid) && React.createElement("div", { className: "cds--form__helper-text helper-text" }, helperText),
            !!isInvalid && React.createElement("div", { className: "cds--form-requirement error-text" }, language.get('forms.requireItem')),
            listView && this.getListBasedItems()));
    }
}
